import greenHotSound from "../../audio/green-hot.wav";
import greenColdSound from "../../audio/green-cold.wav";
import redSound from "../../audio/red.mp3";
import measureSound from "../../audio/measure.mp3";

// Pre-created AudioContext and buffers for instant playback
let audioCtx: AudioContext | null = null;
let successBuffer: AudioBuffer | null = null;
let errorBuffer: AudioBuffer | null = null;
let completeBuffer: AudioBuffer | null = null;
let boxAddBuffer: AudioBuffer | null = null;
let alertBuffer: AudioBuffer | null = null;
let toteFinalisedBuffer: AudioBuffer | null = null;
let milestoneChimeBuffer: AudioBuffer | null = null;
let milestoneFanfareBuffer: AudioBuffer | null = null;
let isWarmedUp = false;
let warmUpPromise: Promise<void> | null = null;

const getAudioContext = (): AudioContext => {
    if (!audioCtx) {
        // `interactive` latencyHint tells the browser to minimise the
        // output buffer at the cost of slightly higher CPU. On Chrome
        // this typically halves output latency vs the default
        // ("balanced"), which is what's perceived as "the success
        // sound takes ages after I scan". Safari ignores the hint
        // gracefully so it's safe to pass.
        const Ctor = (window.AudioContext || (window as any).webkitAudioContext);
        try {
            audioCtx = new Ctor({ latencyHint: "interactive" });
        } catch {
            audioCtx = new Ctor();
        }
    }
    return audioCtx;
};

// Generate success sound: single warm tone — E5 (659Hz) sine, 0.25s
const generateSuccessBuffer = async (ctx: AudioContext): Promise<AudioBuffer> => {
    const duration = 0.25;
    const sampleRate = ctx.sampleRate;
    const length = Math.ceil(sampleRate * duration);
    const offlineCtx = new OfflineAudioContext(1, length, sampleRate);

    const osc = offlineCtx.createOscillator();
    const gain = offlineCtx.createGain();
    osc.type = "sine";
    osc.frequency.value = 659.25; // E5 — warm, clear
    gain.gain.setValueAtTime(0.35, 0);
    gain.gain.exponentialRampToValueAtTime(0.001, duration);
    osc.connect(gain);
    gain.connect(offlineCtx.destination);
    osc.start(0);
    osc.stop(duration);

    return offlineCtx.startRendering();
};

// Generate error sound: two-tone descending buzz — A3 (220Hz) then E3 (165Hz)
const generateErrorBuffer = async (ctx: AudioContext): Promise<AudioBuffer> => {
    const duration = 0.5;
    const sampleRate = ctx.sampleRate;
    const length = Math.ceil(sampleRate * duration);
    const offlineCtx = new OfflineAudioContext(1, length, sampleRate);

    const osc1 = offlineCtx.createOscillator();
    const gain1 = offlineCtx.createGain();
    osc1.type = "square";
    osc1.frequency.value = 220;
    gain1.gain.setValueAtTime(0.25, 0);
    gain1.gain.exponentialRampToValueAtTime(0.001, 0.2);
    osc1.connect(gain1);
    gain1.connect(offlineCtx.destination);
    osc1.start(0);
    osc1.stop(0.2);

    const osc2 = offlineCtx.createOscillator();
    const gain2 = offlineCtx.createGain();
    osc2.type = "square";
    osc2.frequency.value = 165;
    gain2.gain.setValueAtTime(0.3, 0.2);
    gain2.gain.exponentialRampToValueAtTime(0.001, 0.5);
    osc2.connect(gain2);
    gain2.connect(offlineCtx.destination);
    osc2.start(0.2);
    osc2.stop(0.5);

    return offlineCtx.startRendering();
};

// Generate complete sound: ascending two-tone celebration — C6 (1047Hz) then E6 (1319Hz) sine
const generateCompleteBuffer = async (ctx: AudioContext): Promise<AudioBuffer> => {
    const duration = 0.5;
    const sampleRate = ctx.sampleRate;
    const length = Math.ceil(sampleRate * duration);
    const offlineCtx = new OfflineAudioContext(1, length, sampleRate);

    // Tone 1: C6 (1047Hz)
    const osc1 = offlineCtx.createOscillator();
    const gain1 = offlineCtx.createGain();
    osc1.type = "sine";
    osc1.frequency.value = 1047;
    gain1.gain.setValueAtTime(0.3, 0);
    gain1.gain.exponentialRampToValueAtTime(0.001, 0.25);
    osc1.connect(gain1);
    gain1.connect(offlineCtx.destination);
    osc1.start(0);
    osc1.stop(0.25);

    // Tone 2: E6 (1319Hz) — higher, celebratory
    const osc2 = offlineCtx.createOscillator();
    const gain2 = offlineCtx.createGain();
    osc2.type = "sine";
    osc2.frequency.value = 1319;
    gain2.gain.setValueAtTime(0.35, 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.001, 0.5);
    osc2.connect(gain2);
    gain2.connect(offlineCtx.destination);
    osc2.start(0.15);
    osc2.stop(0.5);

    return offlineCtx.startRendering();
};

// Generate tote-finalised sound: ascending 4-note arpeggio (C5→E5→G5→C6)
// — a more conclusive, "sealed and done" feel than playComplete's 2-tone.
// Used on Put Away when the last scan of a tote closes it out.
const generateToteFinalisedBuffer = async (ctx: AudioContext): Promise<AudioBuffer> => {
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    const noteDur = 0.18;
    const overlap = 0.04;
    const duration = notes.length * (noteDur - overlap) + overlap + 0.2;
    const sampleRate = ctx.sampleRate;
    const length = Math.ceil(sampleRate * duration);
    const offlineCtx = new OfflineAudioContext(1, length, sampleRate);

    notes.forEach((freq, i) => {
        const start = i * (noteDur - overlap);
        const osc = offlineCtx.createOscillator();
        const gain = offlineCtx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.3, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + noteDur);
        osc.connect(gain);
        gain.connect(offlineCtx.destination);
        osc.start(start);
        osc.stop(start + noteDur);
    });

    return offlineCtx.startRendering();
};

// Generate milestone chime: a quick three-note rise (G5→C6→E6) for the
// middle tier of the points ladder. Deliberately short and bright — an
// operator crossing 500 is mid-scan, not watching the screen.
const generateMilestoneChimeBuffer = async (ctx: AudioContext): Promise<AudioBuffer> => {
    const notes = [783.99, 1046.5, 1318.5]; // G5, C6, E6
    const noteDur = 0.16;
    const step = 0.09;
    const duration = (notes.length - 1) * step + noteDur + 0.05;
    const sampleRate = ctx.sampleRate;
    const offlineCtx = new OfflineAudioContext(1, Math.ceil(sampleRate * duration), sampleRate);

    notes.forEach((freq, i) => {
        const start = i * step;
        const osc = offlineCtx.createOscillator();
        const gain = offlineCtx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.28, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + noteDur);
        osc.connect(gain);
        gain.connect(offlineCtx.destination);
        osc.start(start);
        osc.stop(start + noteDur);
    });

    return offlineCtx.startRendering();
};

// Generate milestone fanfare: the 1,000-point moment. A rising triad
// (C5-E5-G5-C6) played on triangle for a brassier edge than the sine
// arpeggios, then a sustained C-major chord under it so the last note rings
// rather than stopping dead. ~1.6s — long enough to feel like an occasion,
// short enough that it's over before the next scan lands.
const generateMilestoneFanfareBuffer = async (ctx: AudioContext): Promise<AudioBuffer> => {
    const duration = 1.6;
    const sampleRate = ctx.sampleRate;
    const offlineCtx = new OfflineAudioContext(1, Math.ceil(sampleRate * duration), sampleRate);

    const master = offlineCtx.createGain();
    // Headroom: five voices overlap at the peak and clipping on a tinny
    // handheld speaker sounds like a fault, not a celebration.
    master.gain.value = 0.5;
    master.connect(offlineCtx.destination);

    const run = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    run.forEach((freq, i) => {
        const start = i * 0.11;
        const osc = offlineCtx.createOscillator();
        const gain = offlineCtx.createGain();
        osc.type = "triangle";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.32, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.24);
        osc.connect(gain);
        gain.connect(master);
        osc.start(start);
        osc.stop(start + 0.26);
    });

    // Sustained chord from the top of the run to the end.
    const chordStart = 0.44;
    [523.25, 659.25, 783.99, 1046.5].forEach((freq) => {
        const osc = offlineCtx.createOscillator();
        const gain = offlineCtx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, chordStart);
        gain.gain.exponentialRampToValueAtTime(0.18, chordStart + 0.06);
        gain.gain.exponentialRampToValueAtTime(0.001, duration);
        osc.connect(gain);
        gain.connect(master);
        osc.start(chordStart);
        osc.stop(duration);
    });

    return offlineCtx.startRendering();
};

// Generate box-add sound: quick bright pop — A5 (880Hz) sine, 0.12s
const generateBoxAddBuffer = async (ctx: AudioContext): Promise<AudioBuffer> => {
    const duration = 0.12;
    const sampleRate = ctx.sampleRate;
    const length = Math.ceil(sampleRate * duration);
    const offlineCtx = new OfflineAudioContext(1, length, sampleRate);

    const osc = offlineCtx.createOscillator();
    const gain = offlineCtx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.2, 0);
    gain.gain.exponentialRampToValueAtTime(0.001, duration);
    osc.connect(gain);
    gain.connect(offlineCtx.destination);
    osc.start(0);
    osc.stop(duration);

    return offlineCtx.startRendering();
};

// Generate alert sound: three repeating beeps — B5 (988Hz) triangle, attention-grabbing
const generateAlertBuffer = async (ctx: AudioContext): Promise<AudioBuffer> => {
    const duration = 0.9;
    const sampleRate = ctx.sampleRate;
    const length = Math.ceil(sampleRate * duration);
    const offlineCtx = new OfflineAudioContext(1, length, sampleRate);

    for (let i = 0; i < 3; i++) {
        const start = i * 0.3;
        const osc = offlineCtx.createOscillator();
        const gain = offlineCtx.createGain();
        osc.type = "triangle";
        osc.frequency.value = 988;
        gain.gain.setValueAtTime(0.3, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.15);
        osc.connect(gain);
        gain.connect(offlineCtx.destination);
        osc.start(start);
        osc.stop(start + 0.15);
    }

    return offlineCtx.startRendering();
};

// Play pre-rendered buffer — instant when the context is running.
// When suspended, the source is scheduled at `currentTime` so the
// buffer starts the moment `resume()` completes, instead of waiting
// for the resume promise to flip and then doing the schedule. Saves
// ~1 event-loop turn (and on slow devices, that's perceptible).
const playBuffer = (buffer: AudioBuffer) => {
    try {
        const ctx = getAudioContext();
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        if (ctx.state === "suspended") {
            // Fire resume but don't wait. Scheduling at currentTime
            // means the source plays as soon as the audio graph
            // wakes up.
            ctx.resume().catch(() => {});
        }
        source.start(ctx.currentTime);
    } catch {
        // Silently fail
    }
};

// Keep AudioContext alive — ping every 20s with silent buffer to prevent browser suspension
let keepAliveInterval: NodeJS.Timeout | null = null;

export const startKeepAlive = () => {
    if (keepAliveInterval) return;
    keepAliveInterval = setInterval(() => {
        try {
            const ctx = getAudioContext();
            // Only try to resume after the user has interacted with the page
            // (Chrome autoplay policy suppresses resume() before any gesture).
            if (ctx.state === "suspended" && hasUserGesture) ctx.resume();
            // Play a silent buffer to keep the context active
            const silent = ctx.createBuffer(1, 1, ctx.sampleRate);
            const source = ctx.createBufferSource();
            source.buffer = silent;
            source.connect(ctx.destination);
            source.start(0);
        } catch {}
    }, 20000);
};

export const stopKeepAlive = () => {
    if (keepAliveInterval) {
        clearInterval(keepAliveInterval);
        keepAliveInterval = null;
    }
};

// Live fallback — only used if buffers haven't loaded yet
const playBeepLive = (frequency: number, type: OscillatorType, duration: number, gain: number = 0.3) => {
    try {
        const ctx = getAudioContext();
        if (ctx.state === "suspended") ctx.resume().catch(() => {});
        const osc = ctx.createOscillator();
        const gainNode = ctx.createGain();
        osc.type = type;
        osc.frequency.value = frequency;
        const startAt = ctx.currentTime;
        gainNode.gain.setValueAtTime(gain, startAt);
        gainNode.gain.exponentialRampToValueAtTime(0.001, startAt + duration);
        osc.connect(gainNode);
        gainNode.connect(ctx.destination);
        osc.start(startAt);
        osc.stop(startAt + duration);
    } catch {
        // Silently fail
    }
};

// Tracks whether the user has interacted with the page. Chrome blocks
// AudioContext.resume() before a real user gesture (autoplay policy), so we
// gate the resume call on this flag.
let hasUserGesture = false;

// Warm up: create context and pre-render buffers. Resuming a suspended
// AudioContext before a user gesture trips Chrome's autoplay policy and
// emits a console warning, so we only resume once a gesture has occurred
// (handled by the gesture listener below).
export const warmUp = async () => {
    if (isWarmedUp) return;
    isWarmedUp = true;
    try {
        const ctx = getAudioContext();
        if (ctx.state === "suspended" && hasUserGesture) {
            await ctx.resume();
        }
        const [sBuf, eBuf, cBuf, bBuf, aBuf, tfBuf, mcBuf, mfBuf] = await Promise.all([
            generateSuccessBuffer(ctx),
            generateErrorBuffer(ctx),
            generateCompleteBuffer(ctx),
            generateBoxAddBuffer(ctx),
            generateAlertBuffer(ctx),
            generateToteFinalisedBuffer(ctx),
            generateMilestoneChimeBuffer(ctx),
            generateMilestoneFanfareBuffer(ctx),
        ]);
        successBuffer = sBuf;
        errorBuffer = eBuf;
        completeBuffer = cBuf;
        boxAddBuffer = bBuf;
        alertBuffer = aBuf;
        toteFinalisedBuffer = tfBuf;
        milestoneChimeBuffer = mcBuf;
        milestoneFanfareBuffer = mfBuf;
    } catch {
        // Fallback to live synthesis
    }
};

// Pre-render buffers eagerly at module load — OfflineAudioContext rendering
// doesn't need a user gesture (only ctx.resume() / playback does, gated
// separately by hasUserGesture). Doing this on import means buffers are
// ready before the user reaches any scan page, regardless of which section
// of the app they landed on first.
if (typeof window !== "undefined") {
    const initWarmUp = () => {
        if (!warmUpPromise) {
            warmUpPromise = warmUp();
        }
    };

    initWarmUp();

    // Still listen for the first user gesture so the real AudioContext can
    // be resumed (Chrome's autoplay policy suspends it until then) and so
    // any pages that beat the eager warmUp also have a fallback trigger.
    const events = ["click", "touchstart", "keydown", "pointerdown"];
    const handler = () => {
        hasUserGesture = true;
        try {
            const ctx = getAudioContext();
            if (ctx.state === "suspended") ctx.resume();
        } catch { /* noop */ }
        initWarmUp();
        warmUpPromise?.then(() => {
            events.forEach(e => window.removeEventListener(e, handler));
        });
    };
    events.forEach(e => window.addEventListener(e, handler));
}

/** Single warm tone — E5 sine, 0.25s */
export const playSuccess = () => {
    if (successBuffer) {
        playBuffer(successBuffer);
    } else {
        // Play immediately with live synthesis — no waiting
        playBeepLive(659.25, "sine", 0.25, 0.35);
        // Warm up in background for next time
        warmUp();
    }
};

/** Two-tone descending buzz — A3 → E3 square, 0.5s + vibrate */
export const playError = () => {
    if (errorBuffer) {
        playBuffer(errorBuffer);
    } else {
        playBeepLive(165, "square", 0.3, 0.25);
        warmUp();
    }
    // Vibrate on error — short buzz pattern
    try { navigator.vibrate?.([150, 50, 150]); } catch {}
};

/** Ascending two-tone celebration — C6 → E6 sine, 0.5s */
export const playComplete = () => {
    if (completeBuffer) {
        playBuffer(completeBuffer);
    } else {
        playBeepLive(1319, "sine", 0.4, 0.35);
        warmUp();
    }
};

/** Quick bright pop — A5 sine, 0.12s */
export const playBoxAdd = () => {
    if (boxAddBuffer) {
        playBuffer(boxAddBuffer);
    } else {
        playBeepLive(880, "sine", 0.12, 0.2);
        warmUp();
    }
};

/** Three-tone attention beep — B5 triangle, 0.9s */
export const playAlert = () => {
    if (alertBuffer) {
        playBuffer(alertBuffer);
    } else {
        playBeepLive(988, "triangle", 0.15, 0.3);
        warmUp();
    }
    try { navigator.vibrate?.([200, 100, 200, 100, 200]); } catch {}
};

/** Ascending 4-note arpeggio (C5-E5-G5-C6 sine, ~0.6s) — signals a tote
 *  has been fully finalised / moved to its resting state. Used on Put Away
 *  for the last scan that closes out a tote. */
export const playToteFinalised = () => {
    if (toteFinalisedBuffer) {
        playBuffer(toteFinalisedBuffer);
    } else {
        playBeepLive(1047, "sine", 0.4, 0.35);
        warmUp();
    }
};

/** Three-note rise (G5-C6-E6, ~0.4s) — the 500-point milestone. */
export const playMilestoneChime = () => {
    if (milestoneChimeBuffer) {
        playBuffer(milestoneChimeBuffer);
    } else {
        playBeepLive(1046.5, "sine", 0.2, 0.28);
        warmUp();
    }
};

/** Rising triad over a sustained chord (~1.6s) — the 1,000-point milestone
 *  and every one above it. The only sound in the app long enough to be a
 *  moment rather than a cue. */
export const playMilestoneFanfare = () => {
    if (milestoneFanfareBuffer) {
        playBuffer(milestoneFanfareBuffer);
    } else {
        playBeepLive(1046.5, "triangle", 0.5, 0.3);
        warmUp();
    }
};

export const playBadSound = () => playError();
export const playGoodSound = () => playSuccess();

// WAV preloads
let greenHotAudio: HTMLAudioElement | null = null;
let greenColdAudio: HTMLAudioElement | null = null;
let redAudio: HTMLAudioElement | null = null;
let measureAudio: HTMLAudioElement | null = null;

if (typeof window !== "undefined") {
    greenHotAudio = new Audio(greenHotSound);
    greenHotAudio.preload = "auto";
    greenHotAudio.load();
    greenColdAudio = new Audio(greenColdSound);
    greenColdAudio.preload = "auto";
    greenColdAudio.load();
    redAudio = new Audio(redSound);
    redAudio.preload = "auto";
    redAudio.load();
    measureAudio = new Audio(measureSound);
    measureAudio.preload = "auto";
    measureAudio.load();
}

export const playGreenHotSound = () => {
    if (greenHotAudio) { greenHotAudio.currentTime = 0; greenHotAudio.play().catch(() => {}); }
};

export const playGreenColdSound = () => {
    if (greenColdAudio) { greenColdAudio.currentTime = 0; greenColdAudio.play().catch(() => {}); }
};

export const playRedSound = () => {
    if (redAudio) { redAudio.currentTime = 0; redAudio.play().catch(() => {}); }
};

export const playMeasureSound = () => {
    if (measureAudio) { measureAudio.currentTime = 0; measureAudio.play().catch(() => {}); }
};
